import React, { useState } from "react";

import { useAppendConsent } from "@/api2/Consent/mutations/useAppendConsent";
import { usePolicyMetadataQuery } from "@/api2/Consent/queries/usePolicyMetadata";
import Spinner from "@/components/UI/Spinner";
import { ConsentedPolicyEnum } from "@/core/constants";
import { TaskTypeEnum } from "@/core/constants";
import type { Task } from "@/core/types";
import { getConsentPayload } from "@/lib/helpers/getConsentPayload";

import { ConsentToACP } from "./components/ConsentToACP";
import { ConsentToPolicy } from "./components/ConsentToPolicy";

type Props = {
  onTaskCompleted: (task: Task) => void;
  task: Task;
};

export function ConsentRouter({ onTaskCompleted, task }: Props) {
  const policyType = task.policy_type;
  const { mutateAsync: appendConsent } = useAppendConsent();
  const { data: policyMetadata, isLoading } = usePolicyMetadataQuery();
  const [keyId, setKeyId] = useState(0);

  const onGiveConsent = async (response: boolean = true) => {
    if (!policyMetadata) return;

    const policy = policyMetadata[policyType];
    const consentPayload = getConsentPayload({
      consentResponse: response,
      consentType: ConsentedPolicyEnum[policyType],
      patientId: task.target_patient_id,
      policyMetadata,
      policyVersion: policy.current_version,
    });

    await appendConsent(consentPayload);

    onTaskCompleted(task);
    setKeyId((prev) => prev + 1);
  };

  if (isLoading || !policyMetadata || !task) return <Spinner />;

  switch (policyType) {
    case "ADVANCED_CARE_PROGRAM":
      return (
        <ConsentToACP
          key={`ConsentToACPKey-${keyId}`}
          onSubmit={onGiveConsent}
          policyMetadata={policyMetadata}
        />
      );
    case "CONSENT_TO_TREATMENT":
    case "HIPAA":
    case "PRIVACY":

    case "TERMS_AND_CONDITIONS":
      return (
        <ConsentToPolicy
          key={`ConsentOnLogInKey-${keyId}`}
          onSubmit={onGiveConsent}
          policyMetadata={policyMetadata}
          subTitle={getConsentCopy(task)}
          title={task.label}
        />
      );

    default:
      return <Spinner />; // better than nothing
  }
}

function getConsentCopy(task: Task) {
  const { target_patient_name, task_type } = task;

  switch (task_type) {
    case TaskTypeEnum.UpdatedChildConsent:
      return `We've updated the terms of our policies. By selecting “Agree & Continue"”, you confirm that you have read and agree to the following policies on behalf of your child ${target_patient_name}.`;

    case TaskTypeEnum.UpdatedConsentRequired:
      return "Our records indicate that you recently turned 18. By selecting “Agree & Continue”, you confirm that you have read and agree to the following policies.";

    case TaskTypeEnum.UpdatedPolicies:
      return "We've updated the terms of our policies. By selecting “Agree & Continue”, you confirm that you have read and agree to the following policies.";

    default:
      return "";
  }
}
